import React, { useState, useMemo, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { useStoreState, useStoreActions } from 'easy-peasy';

import useDebounceSearch from '../../../hooks/useDebounchSearch';
import HelperMetiers360 from '../../../services/HelpersMetiers360';
import Loader from '../Loader';

const articleLink = (article, onOpenArticle) => (
    <a href="#" onClick={(e) => { e.preventDefault(); onOpenArticle(article.id); }}>
        {article.title}
    </a>
);

const HelpCenterTree = () => {
    const { tree, isFetchingTree, articleById, isFetchingArticleById } = useStoreState(state => state.helpCenter);
    const { constants } = useStoreState(state => state.params);
    const { setView, fetchHelpCenterTree, fetchArticleById, setArticleById } = useStoreActions(actions => actions.helpCenter);

    const helpCenterUrl = constants?.helpCenterUrl;

    useEffect(() => {
        fetchHelpCenterTree();
    }, []);

    const { searchInput, search } = useDebounceSearch({ placeholder: 'Rechercher dans les titres...', delay: 300 });

    const [openCategoryIds, setOpenCategoryIds] = useState([]);

    const toggleCategory = (id) => {
        setOpenCategoryIds(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
    };

    const matchingArticles = useMemo(() => {
        if (!search) return null;

        const results = [];
        tree.forEach(category => {
            category.folders?.forEach(folder => {
                folder.articles?.forEach(article => {
                    if (HelperMetiers360.isSearchInText(search, article.title)) {
                        results.push(article);
                    }
                });
            });
        });
        return results;
    }, [search, tree]);

    if (isFetchingTree || isFetchingArticleById) {
        return <Loader />;
    }

    if (articleById) {
        const relatedArticles = articleById.articles_lies
            ?.map(relation => relation.related_support_articles_id)
            .filter(relatedArticle => relatedArticle?.status === 'publie') ?? [];

        return (
            <div className="help-center-article">
                <span className="help-center-back-link" onClick={() => setArticleById(null)}>
                    <i className="fas fa-arrow-left" /> Retour à l&apos;aide
                </span>
                <h2 className="help-center-tree-title">{articleById.title}</h2>
                <div className="help-center-article-content" dangerouslySetInnerHTML={{ __html: articleById.content }} />

                {!!relatedArticles.length &&
                    <div className="help-center-related-articles">
                        <h4>Articles liés</h4>
                        <ul>
                            {relatedArticles.map(relatedArticle => (
                                <li key={relatedArticle.id}>{articleLink(relatedArticle, fetchArticleById)}</li>
                            ))}
                        </ul>
                    </div>
                }
            </div>
        );
    }

    return (
        <div className="help-center-tree">
            <h2 className="help-center-tree-title">Centre d&apos;aide</h2>
            <p className="help-center-tree-subtitle">Trouvez rapidement la réponse à vos questions.</p>

            <div className="help-center-search">{searchInput}</div>

            {!tree?.length
                ? <p className="help-center-empty">
                    Aucun article disponible pour le moment.{' '}
                    <a href={helpCenterUrl} target="_blank" rel="noopener noreferrer">
                        Consultez notre centre d&apos;aide
                    </a>
                </p>
                : matchingArticles
                    ? matchingArticles.length
                        ? <ul className="help-center-article-list">
                            {matchingArticles.map(article => <li key={article.id}>{articleLink(article, fetchArticleById)}</li>)}
                        </ul>
                        : <p className="help-center-empty">Aucun article ne correspond à votre recherche.</p>
                    : <>
                        {tree.map(category => {
                            const isOpen = openCategoryIds.includes(category.id);
                            const folders = category.folders?.filter(folder => folder.articles?.length) ?? [];

                            return (
                                <div className="help-center-category" key={category.id}>
                                    <div className="help-center-category-header" onClick={() => toggleCategory(category.id)}>
                                        <div>
                                            <h3>{category.title}</h3>
                                            {category.description && <p>{category.description}</p>}
                                        </div>
                                        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} />
                                    </div>
                                    {isOpen &&
                                        <div className="help-center-folder-grid">
                                            {folders.map(folder => (
                                                <div className="help-center-folder" key={folder.id}>
                                                    <h4>{folder.title}</h4>
                                                    <ul>
                                                        {folder.articles.map(article => <li key={article.id}>{articleLink(article, fetchArticleById)}</li>)}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>
                                    }
                                </div>
                            );
                        })}

                        <div className="help-center-contact-banner">
                            <div>
                                <h4>Vous ne trouvez pas votre réponse ?</h4>
                                <p>Notre équipe support vous répond.</p>
                            </div>
                            <Button variant="primary" onClick={() => setView('contact')}>Contacter le support</Button>
                        </div>
                    </>
            }
        </div>
    );
};

export default HelpCenterTree;
